import React from "react";
import { styled } from "@mui/material/styles";
import { ReactTinyLink } from "react-tiny-link";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Hls from "hls.js";
import PreviewModelViewer from "./PreviewModelViewer";

const PREFIX = "PreviewPlayer";

const classes = {
  documentStyle: `${PREFIX}-documentStyle`,
  videoStyle: `${PREFIX}-videoStyle`,
  imageStyle: `${PREFIX}-imageStyle`,
  messageContainer: `${PREFIX}-messageContainer`,
  messageTitle: `${PREFIX}-messageTitle`,
  messageStyle: `${PREFIX}-messageStyle`,
  optionnalMessage: `${PREFIX}-optionnalMessage`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.documentStyle}`]: {
    height: "100%",
    width: "100%",
  },

  [`& .${classes.videoStyle}`]: {
    height: "50%",
    marginTop: "1rem",
    position: "relative",
    top: "20%",
  },

  [`& .${classes.imageStyle}`]: {
    width: "100%",
    maxWidth: "560px",
    margin: "1rem 0",
  },

  [`& .${classes.messageContainer}`]: {
    margin: "1rem",
  },

  [`& .${classes.messageTitle}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.messageStyle}`]: {
    width: "100%",
    wordBreak: "break-word",
  },

  [`& .${classes.optionnalMessage}`]: {
    marginTop: "1rem",
  },
}));

const PreviewPlayer = (props) => {
  console.log("media =>", props);
  const { t } = useTranslation();
  const videoRef = React.useRef();

  React.useEffect(() => {
    if (props.media?.content.endsWith(".m3u8")) {
      const video = videoRef.current;

      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(props.media.content);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play();
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = props.media.content;
        video.addEventListener("loadedmetadata", () => {
          video.play();
        });
      }
    }
  }, [props.media?.content]);

  if (props.media) {
    if (props.media.type === "link") {
      return (
        <Root>
          <h1 style={{ margin: "3rem 0 2rem 0", textAlign: "center" }}>
            {props.media.title}
          </h1>
          <p style={{ marginBottom: "2rem", textAlign: "center" }}>
            {props.media.description}
          </p>
          <h3 style={{ textAlign: "center" }}>
            &#8595; Click below to follow the link &#8595;
          </h3>
          <ReactTinyLink
            cardSize="small"
            showGraphic={true}
            maxLine={2}
            minLine={1}
            url={props.media.content}
            onClick={() => window.open(props.media.content, "_blank")}
          />
        </Root>
      );
    } else if (
      props.media.type === "video" ||
      props.media.type === "document"
    ) {
      return (
        <Root style={{ minheight: "" }}>
          <div
            className={
              props.media.type === "video"
                ? classes.videoStyle
                : classes.documentStyle
            }
          >
            {props.media.content.includes("videas") &&
            !props.media.content.endsWith(".m3u8") ? (
              <iframe
                width="100%"
                height="300px"
                src={props.media.content}
                title="campaign-video-manager-player"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                ref={videoRef}
                width="100%"
                height="300px"
                src={props.media.content}
                controls
              />
            )}
            {props.media.message && (
              <div className={classes.optionnalMessage}>
                <Typography variant="h6" className={classes.messageTitle}>
                  {t("campaignsManagement.participants.optionalMessage")}
                </Typography>
                <Typography variant="body1" className={classes.messageStyle}>
                  {props.media.message}
                </Typography>
              </div>
            )}
            {props.media.description && (
              <div className={classes.optionnalMessage}>
                <Typography variant="h6" className={classes.messageTitle}>
                  {props.media.title}
                </Typography>
                <Typography variant="body1" className={classes.messageStyle}>
                  {props.media.description}
                </Typography>
              </div>
            )}
          </div>
        </Root>
      );
    } else if (props.media.type === "image") {
      return (
        <Root>
          <img
            src={props.media.content}
            alt={`content-${props.media.content}`}
            className={classes.imageStyle}
          />
          {props.media.message && (
            <div className={classes.optionnalMessage}>
              <Typography variant="h6" className={classes.messageTitle}>
                {t("campaignsManagement.participants.optionalMessage")}
              </Typography>
              <Typography variant="body1" className={classes.messageStyle}>
                {props.media.message}
              </Typography>
            </div>
          )}
          {props.media.description && (
            <div className={classes.optionnalMessage}>
              <Typography variant="h6" className={classes.messageTitle}>
                {props.media.title}
              </Typography>
              <Typography variant="body1" className={classes.messageStyle}>
                {props.media.description}
              </Typography>
            </div>
          )}
        </Root>
      );
    } else if (props.media.type === "model") {
      const { link, usdz, molding, size, imageUrl } = props.media.full.model3D;
      return (
        <PreviewModelViewer
          url={link}
          usdz={usdz}
          title={props.media.title}
          isFrame={!!imageUrl}
          size={size}
          molding={molding}
          imageUrl={imageUrl}
        />
      );
    } else {
      // type is message
      return (
        <Root>
          <div className={classes.messageContainer}>
            <Typography variant="h6" className={classes.messageTitle}>
              {t("campaignsManagement.participants.participantMessage")}
            </Typography>
            <Typography variant="body1" className={classes.messageStyle}>
              {props.media.message}
            </Typography>
          </div>
        </Root>
      );
    }
  } else return null;
};

export default PreviewPlayer;
